/**
 * The Appointment section — `docs/new-request.md` §9, §10.8, §11.3, §12,
 * Boards 2, 6, 7, 8, 9, 15, 16.
 *
 * Before anybody is chosen: *What they want*, *Length*, and *Who goes* — the
 * people the server recommends as cards (nothing chosen until a click), the
 * fallback band when nobody covers the work, and *Someone else…* to search
 * anyone who sells. Until the place and the division are known it says what
 * it is waiting for and anyone can still be searched (§9.1).
 *
 * Once a person is chosen: their card, the **When** box with what the drives
 * either side say (OK · Tight · Late · off — warn, never block, §10.8),
 * Starts and Length, and the confirmation the customer will receive, word for
 * word (§12). Every number here is the server's or `placementOf`'s — a shared
 * rule the server runs too — and the words are `shared`'s.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  ARRIVAL_WINDOWS, LENGTH_STEPS, PREFERS_LABELS, SEGMENT_LABELS,
  arrivalWords, betweenWords, clock, clockBare, clockRange, confirmationEmail,
  confirmationText, dayComma, dayLong, dayShort, driveWords, lateWords, milesWords, minutesWords,
  moveToWords, okWords, placementOf, tightWords, weekdayLong, weekdayShort, workDaysWords,
  type CalendarView, type ConfirmChannel, type ConfirmationFacts, type Placement, type Prefers,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Avatar, Switch } from '../components/primitives.js'
import { Field } from '../components/form/field.js'
import {
  IconBulletList, IconCalendar, IconCheck, IconClose, IconInfo, IconLoop, IconPin, IconPlus, IconSearch, IconWarn,
} from '../frame/icons.js'
import { requestsApi, type Candidate, type RequestSetup, type SomeoneElse } from './api.js'
import { appointmentStatus, channelFor, type AppointmentFields, type SheetState } from './model.js'
import { SectionHeading } from './section-frame.js'
import { KeySelect } from './key-select.js'
import { timeOf, useRequestSheet, type ChosenTime, type PersonRef } from './sheet-context.js'
import { GradeTag, PhonePicker } from './job-kit.js'

// ---------------------------------------------------------------------------
// Summary, for the folded line
// ---------------------------------------------------------------------------

/**
 * The first sentence of *What they want*, or its first words, ending in an
 * ellipsis when there is more (Board 2: *Wants a 16 × 20 paver patio … fire pit…*).
 */
export function firstWords(text: string, max = 70): string {
  const all = text.trim().replace(/\s+/g, ' ')
  if (!all) return ''
  const sentence = /^(.+?[.!?])(\s|$)/.exec(all)?.[1]
  const cut = sentence && sentence.length <= max
    ? sentence.replace(/[.!?]$/, '')
    : all.length <= max ? all : all.slice(0, max).replace(/\s+\S*$/, '')
  return cut.length < all.length ? `${cut}…` : cut
}

/**
 * *Thu, Oct 22 · 10:00–11:30 AM with Tom Reyes* once a time is chosen, else the
 * first words of *What they want* (Board 2 C), else who was chosen.
 */
export function appointmentSummaryOf(state: SheetState, setup?: RequestSetup): string {
  const appointment = state.appointment
  const estimate = setup?.divisions.find((one) => one.code === state.job.divisionCode)?.estimateMinutes
  const minutes = appointment.minutes ?? estimate ?? 60
  if (appointment.salespersonName && appointment.date && appointment.start !== null) {
    return `${dayComma(appointment.date)} · ${clockRange(appointment.start, appointment.start + minutes)}`
      + ` with ${appointment.salespersonName}`
  }
  if (appointment.description.trim()) return firstWords(appointment.description)
  if (appointment.salespersonName) return `${appointment.salespersonName} · no time yet`
  return ''
}

const firstOf = (name: string | null | undefined): string => name?.trim().split(/\s+/)[0] ?? ''

const refOf = (person: { id: string; name: string; firstName?: string }): PersonRef =>
  ({ id: person.id, name: person.name, firstName: person.firstName ?? firstOf(person.name) })

/** The phone card's one line (Board 15 B): *Thu: a 12:30 stop 1.6 mi away*, else the most telling reason. */
function phoneReason(candidate: Candidate): string | null {
  const near = candidate.best?.near
  if (candidate.best && near) {
    return `${weekdayShort(candidate.best.date)}: a ${clockBare(near.start)} stop ${milesWords(near.miles)} away`
  }
  return candidate.reasons.find((reason) => reason === 'Sold to them before')
    ?? candidate.reasons.find((reason) => /open request/.test(reason))
    ?? candidate.reasons[0] ?? null
}

/** The glyph beside a reason (Board 6): a tick for coverage, a pin for nearness, a list for requests. */
function ReasonGlyph({ reason }: { reason: string }) {
  /* *Near the property on Thu — …* on a desktop, *Thu: a 12:30 stop 1.6 mi away* on a phone (15 B). */
  if (reason.startsWith('Near') || / stop .* away$/.test(reason)) return <IconPin size={12} />
  if (/open request/.test(reason)) return <IconBulletList size={12} />
  if (reason === 'Sold to them before') return <IconLoop size={12} />
  return <IconCheck size={12} />
}

/** Two people: what the recommendation is waiting to show (Board 2). */
function PeopleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} aria-hidden fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-faint">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M17 14.5c2.4 0 4 1.6 4 4" />
    </svg>
  )
}

/** *Compare calendars* — three columns, as Board 6 draws it. */
function ColumnsGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} aria-hidden fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16M15 4v16" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

export function AppointmentSection() {
  const api = useRequestSheet()
  const { phone } = api
  const appointment = api.state.appointment
  const ready = appointmentStatus(api.state) === 'ready'
  const chosen: PersonRef | null = appointment.salespersonId
    ? { id: appointment.salespersonId, name: appointment.salespersonName ?? '',
      firstName: firstOf(appointment.salespersonName) }
    : null
  const timed = Boolean(chosen && appointment.startsAt && appointment.date && appointment.start !== null)

  return (
    <section aria-label="Appointment">
      <SectionHeading section="appointment" ready={ready} sub={chosen ? undefined : 'a site visit'} />
      {chosen ? <Chosen person={chosen} timed={timed} /> : (
        <>
          <WhatTheyWant hint={!phone} />
          <WhoGoes />
        </>
      )}
    </section>
  )
}

function setAppointment(
  update: (change: (state: SheetState) => SheetState) => void, change: Partial<AppointmentFields>,
) {
  update((state) => ({ ...state, appointment: { ...state.appointment, ...change } }))
}

/** *What they want* (§9.1). */
function WhatTheyWant({ hint }: { hint: boolean }) {
  const api = useRequestSheet()
  return (
    <Field label="What they want"
      hint={hint ? 'goes on the appointment and on the job as “What they asked for”' : undefined}>
      {(ids) => (
        <textarea
          id={ids.id}
          aria-describedby={ids.describedBy}
          value={api.state.appointment.description}
          data-request-field="description"
          onChange={(event) => setAppointment(api.update, { description: event.target.value })}
          className={cn('w-full resize-none rounded-control border border-line bg-surface px-3 py-2.5',
            'text-[13px] leading-[1.5] text-text outline-none focus-visible:shadow-none placeholder:text-text-faint',
            'focus:border-accent-line focus:ring-2 focus:ring-accent-ring',
            api.phone ? 'h-[78px]' : 'h-[66px]')}
        />
      )}
    </Field>
  )
}

/** *Length*: 15-minute steps, starting at the division's estimate visit (§9.1). */
function LengthField({ width }: { width?: string }) {
  const api = useRequestSheet()
  const { facts } = api
  const picked = api.state.appointment.minutes
  const value = picked ?? facts.divisionMinutes
  const options = LENGTH_STEPS.map((minutes) => ({ value: String(minutes), label: minutesWords(minutes) }))
  const hint = facts.divisionName && (picked === null || picked === facts.divisionMinutes)
    ? `${facts.divisionName}’s estimate visit` : undefined
  const change = (next: string | null) =>
    setAppointment(api.update, { minutes: next === null ? null : Number(next) })
  return (
    <Field label="Length" hint={hint} className={width}>
      {(ids) => (api.phone
        ? (
          <PhonePicker label="Length" value={value === null ? null : String(value)} options={options}
            placeholder="from the division" onChange={change} />
        )
        : (
          <KeySelect id={ids.id} describedBy={ids.describedBy} label="Length"
            value={value === null ? null : String(value)} options={options}
            placeholder="from the division" onChange={change} />
        ))}
    </Field>
  )
}

// ---------------------------------------------------------------------------
// Who goes, before anybody is chosen
// ---------------------------------------------------------------------------

function WhoGoes() {
  const api = useRequestSheet()
  const { facts, phone } = api
  const recommendation = api.recommendation
  const known = Boolean(facts.place && facts.orgUnitId)
  const hint = api.hintFor('time')

  /* §9.1: the facts recommendations wait for, said rather than greyed. */
  if (!known || !facts.divisionCode) {
    const waiting = !known && !facts.divisionCode
      ? 'Add an address and a division to see who’s recommended — or search anyone.'
      : !known
        ? `Add an address to see who’s recommended for ${facts.divisionName} — or search anyone.`
        : 'Add a division to see who’s recommended — or search anyone.'
    return (
      <>
        <div className={cn('mt-3', phone ? 'flex flex-col gap-2.5' : 'grid grid-cols-[170px_1fr] gap-x-3.5 gap-y-3')}>
          <LengthField />
          <Field label="Who goes" group>
            {() => <SomeoneElseSearch placeholder="Search anyone who sells…" />}
          </Field>
        </div>
        <div className={cn('mt-2.5 flex items-center gap-2 rounded-[10px] border border-dashed',
          'border-line-strong bg-surface-alt px-3 py-2.5 text-[12px] leading-[15px] text-text-muted')}>
          <PeopleGlyph />
          {waiting}
        </div>
        {hint && <TimeHint hint={hint} />}
      </>
    )
  }

  const people = recommendation?.people ?? []
  const firstThree = people.slice(0, 3).map(refOf)

  return (
    <>
      <div className="mt-3"><LengthField width={phone ? undefined : 'w-[200px]'} /></div>

      {/* A phone's label sits 4px tighter above and below (Board 15 B: 26 and 16). */}
      <div className={phone ? 'mt-2' : 'mt-3'}>
        <div className={cn('flex items-center gap-2', phone ? 'mb-0.5' : 'mb-1.5')}>
          {!recommendation?.fallback && (
            <span className="text-fieldlabel text-text-muted">Who goes</span>
          )}
          <span className="flex-1" />
          {phone
            ? (
              <button type="button" onClick={() => api.compareCalendars(firstThree)}
                disabled={firstThree.length === 0}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-accent">
                <ColumnsGlyph />
                Side by side
              </button>
            )
            : (
              <>
                {recommendation && !recommendation.fallback && (
                  <span className="text-[11.5px] text-text-faint">
                    {recommendation.covered} of {recommendation.teamSize} cover this
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => api.compareCalendars(firstThree)}
                  disabled={firstThree.length === 0}
                  className={cn('inline-flex h-7 items-center gap-1.5 rounded-button border border-line',
                    'bg-surface px-3.5 text-[12px] font-semibold text-text hover:border-line-strong')}
                >
                  <ColumnsGlyph />
                  Compare calendars
                </button>
              </>
            )}
        </div>

        {recommendation?.fallback && <FallbackBand />}

        {api.recommendationLoading && !recommendation && (
          <div className="py-2 text-[12px] text-text-faint">Finding who’s recommended…</div>
        )}

        <div className="flex flex-col gap-2">
          {people.map((candidate) => (
            <CandidateCard key={candidate.id} candidate={candidate} />
          ))}
        </div>

        <div className="mt-2">
          <SomeoneElseSearch placeholder="Someone else…" />
        </div>
        {hint && <TimeHint hint={hint} />}
      </div>
    </>
  )
}

/** Board 6 B: nobody covers this, so everybody in the branch is shown. */
function FallbackBand() {
  const api = useRequestSheet()
  const recommendation = api.recommendation!
  return (
    <div className={cn('mb-2.5 flex items-start gap-[9px] rounded-[10px] border border-amber-band-line',
      'bg-amber-band-bg px-3 py-2.5 text-[12px] leading-[1.45] text-amber-band-text')}>
      <span className="mt-px shrink-0"><IconInfo size={15} /></span>
      <div>
        <b>
          Nobody covers {recommendation.divisionName} · {SEGMENT_LABELS[recommendation.segment]} in{' '}
          {recommendation.branchName} yet.
        </b>
        {' '}Showing every salesperson who works in {recommendation.branchName}.{' '}
        {api.setup.canEditCoverage
          ? <a href="/settings/sales-coverage" className="font-bold text-inherit underline">Set up sales coverage</a>
          : <b className="underline">Set up sales coverage</b>}
      </div>
    </div>
  )
}

/** One recommended person (Board 6, Board 15 B). Nothing is chosen until a click. */
function CandidateCard({ candidate }: { candidate: Candidate }) {
  const api = useRequestSheet()
  const { phone } = api
  const person = refOf(candidate)
  const best = candidate.best

  const pickPerson = () => { api.choose(person, null); api.openCalendar(person) }
  const pickTime = () => {
    if (!best) return
    api.choose(person, timeOf(best))
    api.openCalendar(person)
  }

  const reasons = phone ? [phoneReason(candidate)].filter((line): line is string => Boolean(line))
    : candidate.reasons

  return (
    <div
      role={phone ? 'button' : 'radio'}
      aria-checked={phone ? undefined : false}
      tabIndex={0}
      aria-label={candidate.name}
      onClick={pickPerson}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); pickPerson() }
      }}
      className={cn('flex cursor-pointer items-start gap-3 rounded-[12px] border-[1.5px] border-line',
        'bg-surface px-3.5 py-3 text-left hover:border-line-strong')}
    >
      {!phone && (
        <span aria-hidden className="mt-[9px] inline-block h-[18px] w-[18px] shrink-0 rounded-full border-[1.5px] border-line-strong bg-surface" />
      )}
      <Avatar name={candidate.name} size={36} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1.5">
          <b className="truncate text-[14px] font-bold text-text">{candidate.name}</b>
          {!phone && candidate.recommended && (
            <span className="inline-flex h-[19px] items-center whitespace-nowrap rounded-tag bg-accent-soft px-1.5 text-[10.5px] font-semibold text-accent-dark">
              Recommended
            </span>
          )}
        </div>
        {/* Tight lines, 18px apart, each with its glyph — a phone's too (Boards 6, 15 B). */}
        {reasons.map((reason) => (
          <div key={reason} className="mt-[3px] flex items-start gap-1.5 text-[12px] leading-tight text-text-body">
            <span className="mt-px shrink-0 text-text-muted"><ReasonGlyph reason={reason} /></span>
            <span className="min-w-0">{reason}</span>
          </div>
        ))}
      </div>
      <div className="flex shrink-0 flex-col items-end">
        {best && <GradeTag grade={best.grade} />}
        {best && (
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); pickTime() }}
            className="mt-1.5 inline-flex flex-col items-end gap-px rounded-[9px] border border-accent-line bg-surface px-[9px] py-[5px] hover:bg-green-band-bg"
          >
            {/* 15 + 1 + 13 inside 5px and a border: the 41px button of Boards 6 and 15 B. */}
            <b className="whitespace-nowrap text-[12.5px] font-bold leading-[15px] text-text">
              {phone ? `${weekdayShort(best.date)} ${clock(best.start)}` : `${dayShort(best.date)}, ${clock(best.start)}`}
            </b>
            <span className="whitespace-nowrap text-[11px] leading-[13px] text-text-muted">
              {phone ? `+${best.added} min` : `+${best.added} min drive`}
            </span>
          </button>
        )}
        <SeeCalendar person={person} />
      </div>
    </div>
  )
}

function SeeCalendar({ person }: { person: PersonRef }) {
  const api = useRequestSheet()
  return (
    <button
      type="button"
      onClick={(event) => { event.stopPropagation(); api.openCalendar(person) }}
      className="mt-[7px] inline-flex items-center gap-[5px] whitespace-nowrap text-[12px] font-semibold leading-[15px] text-accent hover:underline"
    >
      <IconCalendar size={13} />
      See calendar
    </button>
  )
}

/**
 * *Someone else…* (Board 6 C): anyone who sells in a branch the person can
 * see, each with why they weren't recommended. Choosing one makes them Who
 * goes and opens their calendar; *See calendar* only opens it.
 */
function SomeoneElseSearch({ placeholder }: { placeholder: string }) {
  const api = useRequestSheet()
  const { facts, phone } = api
  const [text, setText] = useState('')
  const [found, setFound] = useState<SomeoneElse[] | null>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  useEffect(() => {
    const wanted = text.trim()
    if (!wanted) { setFound(null); return }
    let live = true
    const timer = window.setTimeout(() => {
      requestsApi.someoneElse({
        q: wanted, orgUnitId: facts.orgUnitId, division: facts.divisionCode,
        segment: facts.divisionCode ? facts.segment : null,
      })
        .then((answer) => { if (live) { setFound(answer.items); setOpen(true); setActive(0) } })
        .catch(() => { if (live) setFound([]) })
    }, 150)
    return () => { live = false; window.clearTimeout(timer) }
  }, [text, facts.orgUnitId, facts.divisionCode, facts.segment])

  const pick = (person: SomeoneElse) => {
    const ref = refOf(person)
    api.choose(ref, null)
    api.openCalendar(ref)
    setText('')
    setFound(null)
    setOpen(false)
  }

  return (
    <div className="relative">
      <span className={cn('flex w-full items-center gap-2.5 rounded-[11px] border-[1.5px] border-line bg-surface px-3.5',
        'focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]',
        phone ? 'h-11' : 'h-9')}>
        <span className="inline-flex text-text-faint"><IconSearch size={18} /></span>
        <input
          value={text}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => setText(event.target.value)}
          onFocus={() => { if (found) setOpen(true) }}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={(event) => {
            const rows = found ?? []
            if (event.key === 'ArrowDown') { event.preventDefault(); setActive((at) => Math.min(at + 1, rows.length - 1)) }
            else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((at) => Math.max(at - 1, 0)) }
            else if (event.key === 'Enter' && open && rows[active]) { event.preventDefault(); pick(rows[active]!) }
            else if (event.key === 'Escape' && open) { event.stopPropagation(); setOpen(false) }
          }}
          className={cn('min-w-0 flex-1 bg-transparent font-medium text-text outline-none focus-visible:shadow-none placeholder:text-text-faint',
            phone ? 'text-[16px]' : 'text-[15px]')}
        />
      </span>
      {open && found && (
        <div role="listbox" aria-label="Salespeople"
          className="absolute inset-x-0 top-full z-30 mt-1.5 rounded-[12px] border border-line bg-surface pb-1.5 pt-1 shadow-popover">
          <div className="px-[18px] pb-1 pt-2 text-label uppercase text-text-faint">Salespeople</div>
          {found.length === 0 && (
            <div className="px-[18px] py-2 text-[12.5px] text-text-muted">Nobody matches.</div>
          )}
          {found.map((person, index) => (
            <div
              key={person.id}
              role="option"
              aria-selected={index === active}
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => pick(person)}
              className={cn('mx-1.5 flex cursor-pointer items-center gap-[11px] rounded-[9px] px-3 py-2',
                index === active && 'bg-green-band-bg shadow-[inset_0_0_0_1.5px_rgb(var(--color-accent-line))]')}
            >
              <Avatar name={person.name} size={30} />
              <span className="flex min-w-0 flex-1 flex-col">
                <b className="truncate text-[13px] font-semibold text-text">{person.name}</b>
                <span className="truncate text-[11.5px] text-text-muted">{person.why}</span>
              </span>
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); api.openCalendar(refOf(person)); setOpen(false) }}
                className="whitespace-nowrap text-[11.5px] font-semibold text-accent hover:underline"
              >
                See calendar
              </button>
            </div>
          ))}
          <div className="mt-1 border-t border-line-faint px-3.5 pb-0.5 pt-2 text-[11.5px] text-text-faint">
            anyone with the Sales role in branches you can see
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Create pressed without a time (§11.3, Board 9 A) — the dashed amber line.
 * Book mode has no *Create, book later* (§11.4), so there the line stops at
 * the person.
 */
function TimeHint({ hint, quiet, flush }: { hint: string; quiet?: boolean; flush?: boolean }) {
  const { book } = useRequestSheet()
  return (
    <div
      tabIndex={-1}
      data-request-field="time"
      /* `flush` sits right under its *When* label, 5px below it (Board 9 A). */
      className={cn('flex items-center gap-2.5 rounded-[10px] border-[1.5px] border-dashed px-3 py-2.5',
        'text-[12.5px] outline-none', !flush && 'mt-2.5',
        quiet ? 'border-line-strong bg-surface-alt text-text-muted'
          : 'border-amber-dash bg-amber-soft text-amber-band-text')}
    >
      <span className="shrink-0"><IconCalendar size={15} /></span>
      {book
        ? <span>{hint.replace(/ — or press$/, '.')}</span>
        : <span>{hint} <b>Create, book later</b>.</span>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// A person chosen
// ---------------------------------------------------------------------------

function Chosen({ person, timed }: { person: PersonRef; timed: boolean }) {
  const api = useRequestSheet()
  const { phone, facts } = api
  const appointment = api.state.appointment
  const candidate = api.recommendation?.people.find((one) => one.id === person.id) ?? null
  const hint = api.hintFor('time')

  const plan = api.chosenPlan && api.chosenPlan.date === appointment.date ? api.chosenPlan : null
  const placement = plan && timed ? placementOf(plan, appointment.start!, facts.minutes) : null

  const nothingFits = Boolean(!timed && !api.schedule && api.recommendation && candidate && !candidate.best)

  /*
   * Choosing somebody never moves the fields above them (Aaron, step 28):
   * *What they want* and *Length* stay where they were, the chosen person's
   * card sits where the cards were, and *When* comes under it.
   */
  return (
    <>
      <WhatTheyWant hint={!phone} />
      <div className="mt-3"><LengthField width={phone ? undefined : 'w-[200px]'} /></div>
      <div className="mt-3">
        {phone && timed
          ? <PhoneWhen person={person} placement={placement} />
          : <ChosenCard person={person} candidate={candidate} />}
      </div>
      {nothingFits && <NothingFits person={person} />}

      {!(phone && timed) && (timed || !phone || hint) && (
        <div className="mt-3">
          <span className="mb-[5px] block text-fieldlabel text-text-muted">When</span>
          {timed
            ? <WhenBox placement={placement} />
            : (
              <TimeHint
                flush
                quiet={!hint}
                hint={hint ?? `Pick a time for ${person.firstName} — or press`}
              />
            )}
        </div>
      )}
      {timed && <Bands person={person} placement={placement} />}
      {timed && !phone && <div className="mt-2.5 w-[200px]"><StartsField /></div>}
      {timed && <Confirmation />}
    </>
  )
}

/** The chosen person, as Board 7 draws them — selected, one reason. */
function ChosenCard({ person, candidate }: { person: PersonRef; candidate: Candidate | null }) {
  const api = useRequestSheet()
  const { phone } = api
  let reason = candidate?.reasons[0] ?? null
  /* Board 16 D: a phone says *Covers Hardscape · Residential* without the branch. */
  if (reason && phone) reason = reason.replace(/ in [^·]+$/, '')

  const change = () => {
    api.update((state) => ({
      ...state,
      appointment: { ...state.appointment, salespersonId: null, salespersonName: null,
        startsAt: null, date: null, start: null },
    }))
    api.setBookAnyway(null)
  }

  /*
   * Boards 7 and 16 D draw the chosen card with nothing on its right, so the
   * card itself is the way to change who goes: on a desktop it opens that
   * person's calendar, whose chips offer the others and *Someone else*; on a
   * phone it goes back to the cards (§9.2). A question for Aaron (step 28).
   */
  const reopen = () => {
    if (phone) change()
    else api.openCalendar(person)
  }
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={phone ? `Change who goes — ${person.name}` : `${person.name}’s calendar`}
      onClick={reopen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); reopen() }
      }}
      className={cn('flex cursor-pointer items-start gap-3 rounded-[12px] border-[1.5px] border-accent bg-green-band-bg',
        'px-3.5 py-3 shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]')}>
      <Avatar name={person.name} size={36} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1.5">
          <b className="truncate text-[14px] font-bold leading-[17px] text-text">{person.name}</b>
          {!phone && candidate?.recommended && (
            <span className="inline-flex h-[19px] items-center whitespace-nowrap rounded-tag bg-accent-soft px-1.5 text-[10.5px] font-semibold text-accent-dark">
              Recommended
            </span>
          )}
        </div>
        {reason && (
          /* 17 + 3 + 15 keeps the text inside the 36px avatar's height: a 63px card, as drawn (7 A). */
          <div className="mt-[3px] flex items-center gap-1.5 text-[12px] leading-[15px] text-text-body">
            {/* Board 16 D keeps the ✓ on a phone too: “✓ Covers Hardscape · Residential”. */}
            <span className="shrink-0 text-text-muted"><ReasonGlyph reason={reason} /></span>
            <span className="min-w-0">{reason}</span>
          </div>
        )}
      </div>
    </div>
  )
}

/** The key one placement is known by, for *Keep* and *Book it anyway* (§10.8, once). */
const placementKey = (personId: string, placement: Placement): string =>
  `${personId}|${placement.start}|${placement.end}`

/** The When box (Board 7, Board 8): the time, both drives, and a mark for the state. */
function WhenBox({ placement }: { placement: Placement | null }) {
  const api = useRequestSheet()
  const appointment = api.state.appointment
  const first = firstOf(appointment.salespersonName)
  const plan = api.chosenPlan
  const state = placement?.state ?? null
  const title = `${dayComma(appointment.date!)} · ${clockRange(appointment.start!, appointment.start! + api.facts.minutes)}`

  let sub: string | null = null
  if (placement && plan) {
    if (state === 'ok') sub = placement.before || placement.after ? okWords(placement) : null
    else if (state === 'tight') sub = tightWords(placement).title
    else if (state === 'late') sub = lateWords(placement, plan, first).title
    else if (state === 'off') sub = `${first} is off on ${weekdayLong(appointment.date!)}.`
    else if (state === 'outside') sub = `Outside ${first}’s working hours.`
  } else if (!placement) {
    sub = 'Checking the drives…'
  }

  return (
    <div className={cn('flex items-center gap-2.5 rounded-[10px] border-[1.5px] px-3 py-2.5',
      state === 'ok' ? 'border-accent bg-green-band-bg'
        : state === 'tight' ? 'border-amber-dash bg-amber-soft'
          : state === 'late' ? 'border-danger-line bg-danger-bg'
            : state === 'off' || state === 'outside' ? 'border-slate-band-line bg-slate-band-bg'
              : 'border-line bg-surface')}>
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-button border border-accent-line bg-accent-soft text-accent-dark">
        <IconCalendar size={16} />
      </span>
      {/* 17 + 16 beside a 32px tile: the 56px box of Board 7 A. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <b className="text-[14px] font-bold leading-[17px] text-text">{title}</b>
        {sub && (
          <span className={cn('text-[12px] leading-[16px]',
            state === 'ok' ? 'text-accent-dark'
              : state === 'tight' ? 'text-amber-band-text'
                : state === 'late' ? 'text-danger'
                  : state === 'off' || state === 'outside' ? 'text-slate-band-text' : 'text-text-faint')}>
            {sub}
          </span>
        )}
      </div>
      <StateMark state={state} />
    </div>
  )
}

function StateMark({ state }: { state: Placement['state'] | null }) {
  if (state === 'ok') {
    return (
      <span aria-hidden className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 border-accent text-accent">
        <IconCheck size={10} />
      </span>
    )
  }
  if (state === 'tight') return <span className="shrink-0 text-amber-band-text"><IconWarn size={18} /></span>
  if (state === 'late') return <span className="shrink-0 text-danger"><IconWarn size={18} /></span>
  return null
}

/** Board 16 B: the phone's When — the person, the day and both drives in one card. */
function PhoneWhen({ person, placement }: { person: PersonRef; placement: Placement | null }) {
  const api = useRequestSheet()
  const appointment = api.state.appointment
  const state = placement?.state ?? null
  const parts: string[] = []
  if (placement?.before) parts.push(`${driveWords(placement.before.drive)} before`)
  if (placement?.after) parts.push(`${driveWords(placement.after.drive)} after`)
  const sub = state === 'ok' && parts.length > 0 ? `${parts.join(' · ')} — enough both ways`
    : state === 'tight' && placement ? tightWords(placement).title
      : state === 'late' && placement && api.chosenPlan
        ? lateWords(placement, api.chosenPlan, person.firstName).title
        : state === 'off' ? `${person.firstName} is off on ${weekdayLong(appointment.date!)}.`
          : state === 'outside' ? `Outside ${person.firstName}’s working hours.`
            : placement ? null : 'Checking the drives…'
  return (
    <button
      type="button"
      onClick={() => api.openCalendar(person)}
      className={cn('flex w-full items-center gap-2.5 rounded-[12px] border-[1.5px] px-3 py-2.5 text-left',
        state === 'tight' ? 'border-amber-dash bg-amber-soft'
          : state === 'late' ? 'border-danger-line bg-danger-bg'
            : state === 'off' || state === 'outside' ? 'border-slate-band-line bg-slate-band-bg'
              : 'border-accent bg-green-band-bg')}
    >
      <Avatar name={person.name} size={30} />
      <div className="flex min-w-0 flex-col">
        <b className="text-[14px] font-bold text-text">
          {dayComma(appointment.date!)} · {clockRange(appointment.start!, appointment.start! + api.facts.minutes, true)}
        </b>
        {sub && (
          <span className={cn('text-[12px]',
            state === 'tight' ? 'text-amber-band-text' : state === 'late' ? 'text-danger'
              : state === 'off' || state === 'outside' ? 'text-slate-band-text' : 'text-accent-dark')}>
            {sub}
          </span>
        )}
      </div>
    </button>
  )
}

/**
 * The band under the When box for a time that is tight, late or off (§10.8,
 * Board 8 A, B, F) — with the fix one click away, and never a block.
 */
function Bands({ person, placement }: { person: PersonRef; placement: Placement | null }) {
  const api = useRequestSheet()
  const appointment = api.state.appointment
  const [kept, setKept] = useState<string | null>(null)
  const off = placement?.state === 'off' || placement?.state === 'outside'
  const view = useCalendarOf(person, off)
  if (!placement || !api.chosenPlan) return null
  /*
   * While the calendars are on screen the band is theirs (Board 8 draws it
   * beside the day), and the When box above only changes colour — one band,
   * not two. With them closed it is said here, under the box.
   */
  if (api.schedule) return null
  const key = placementKey(person.id, placement)

  const move = (to: number) => {
    const shift = (to - appointment.start!) * 60_000
    api.moveTo({ date: appointment.date!, start: to,
      startsAt: new Date(Date.parse(appointment.startsAt!) + shift).toISOString() })
  }

  if (placement.state === 'tight' && kept !== key) {
    const words = tightWords(placement)
    return (
      <>
        <Band tone="amber" title={words.title} body={words.body} />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {placement.moveTo !== null && placement.moveTo !== placement.start && (
            <SmallButton onClick={() => move(placement.moveTo!)}>{moveToWords(placement.moveTo)}</SmallButton>
          )}
          <SmallButton quiet onClick={() => setKept(key)}>Keep {clockBare(placement.start)}</SmallButton>
        </div>
      </>
    )
  }
  if (placement.state === 'late' && api.bookAnyway !== key) {
    const words = lateWords(placement, api.chosenPlan, person.firstName)
    return (
      <>
        <Band tone="red" title={words.title} body={words.body} />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {placement.moveTo !== null && (
            <SmallButton onClick={() => move(placement.moveTo!)}>{moveToWords(placement.moveTo)}</SmallButton>
          )}
          <SmallButton quiet onClick={() => api.setBookAnyway(key)}>Book it anyway</SmallButton>
        </div>
      </>
    )
  }
  if (off) {
    const who = view?.person
    const days = who ? `${person.firstName}’s working days are ${workDaysWords(who.workDays)}, `
      + `${clock(who.workStart)}–${clock(who.workEnd)}.` : ''
    const title = placement.state === 'off'
      ? `${person.firstName} is off on ${weekdayLong(appointment.date!)}.`
      : `${clockRange(placement.start, placement.end, true)} is outside ${person.firstName}’s working hours.`
    return <Band tone="slate" title={title} body={days} />
  }
  return null
}

function Band({ tone, title, body, children }: {
  tone: 'amber' | 'red' | 'slate' | 'grey'
  title: string
  body?: string
  children?: ReactNode
}) {
  return (
    <div role={tone === 'red' ? 'alert' : undefined}
      className={cn('mt-2 flex items-start gap-[9px] rounded-[10px] border px-3 py-2.5 text-[12px] leading-[1.45]',
        tone === 'amber' ? 'border-amber-band-line bg-amber-band-bg text-amber-band-text'
          : tone === 'red' ? 'border-danger-line bg-danger-bg text-danger'
            : tone === 'slate' ? 'border-slate-band-line bg-slate-band-bg text-slate-band-text'
              : 'border-line bg-line-soft text-text-muted')}>
      <span className="mt-px shrink-0"><IconInfo size={15} /></span>
      <div><b>{title}</b>{body ? <> {body}</> : null}{children}</div>
    </div>
  )
}

function SmallButton({ onClick, quiet, children }: { onClick: () => void; quiet?: boolean; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={cn('inline-flex h-8 items-center justify-center whitespace-nowrap rounded-button text-[12.5px] font-semibold',
        quiet ? 'px-2 text-text-muted hover:text-text'
          : 'border border-line bg-surface px-3.5 text-text hover:border-line-strong')}>
      {children}
    </button>
  )
}

/**
 * A person's calendar, asked for only when a band needs what the day plan
 * doesn't carry — their working days, or the next ten days' first date.
 */
function useCalendarOf(person: PersonRef, wanted: boolean): CalendarView | null {
  const api = useRequestSheet()
  const { facts } = api
  const date = api.state.appointment.date
  const [view, setView] = useState<CalendarView | null>(null)
  useEffect(() => {
    if (!wanted) return
    let live = true
    requestsApi.calendar({
      session: api.requestId, person: person.id, minutes: facts.minutes,
      lat: facts.place?.lat ?? null, lng: facts.place?.lng ?? null,
      orgUnitId: facts.orgUnitId, division: facts.divisionCode,
      segment: facts.divisionCode ? facts.segment : null, day: date,
    })
      .then((answer) => { if (live) setView(answer) })
      .catch(() => { if (live) setView(null) })
    return () => { live = false }
  }, [wanted, person.id, date, api.requestId, facts.minutes, facts.place?.lat, facts.place?.lng,
    facts.orgUnitId, facts.divisionCode, facts.segment])
  return view
}

/**
 * Board 8 C, in the form while no calendar is open: nothing fits in this
 * person's two weeks — the next recommended person, side by side, or further out.
 */
function NothingFits({ person }: { person: PersonRef }) {
  const api = useRequestSheet()
  const view = useCalendarOf(person, true)
  const next = api.recommendation?.people.find((one) => one.id !== person.id && one.best) ?? null
  return (
    <div className="mt-2.5">
      <Band tone="amber" title={`Nothing fits in ${person.firstName}’s next two weeks`}
        body={`— every opening is too short for ${minutesWords(api.facts.minutes)} with the drives.`} />
      {next && <div className="mt-2.5"><CandidateCard candidate={next} /></div>}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {next && (
          <SmallButton onClick={() => api.compareCalendars([person, refOf(next)])}>
            Side by side with {next.firstName}
          </SmallButton>
        )}
        {view && (
          <SmallButton quiet onClick={() => api.setSchedule(() => ({
            mode: 'one', people: [person], day: null, first: view.next,
          }))}>
            {person.firstName} from {dayShort(view.next)}
          </SmallButton>
        )}
      </div>
    </div>
  )
}

/** *Starts*: the chosen time, typed or picked; moving it re-checks the drives (§10.8). */
function StartsField() {
  const api = useRequestSheet()
  const appointment = api.state.appointment
  const start = appointment.start!
  const plan = api.chosenPlan
  const from = Math.max(0, Math.min(plan?.workStart ?? 360, start) - 60)
  const to = Math.min(24 * 60 - 15, Math.max(plan?.workEnd ?? 1200, start) + 60)
  const options = useMemo(() => {
    const steps: number[] = []
    for (let at = from - (from % 15); at <= to; at += 15) steps.push(at)
    if (!steps.includes(start)) steps.push(start)
    return steps.sort((a, b) => a - b).map((at) => ({ value: String(at), label: clock(at) }))
  }, [from, to, start])
  const change = (next: string | null) => {
    if (next === null) return
    const at = Number(next)
    const shift = (at - start) * 60_000
    const time: ChosenTime = { date: appointment.date!, start: at,
      startsAt: new Date(Date.parse(appointment.startsAt!) + shift).toISOString() }
    api.moveTo(time)
  }
  return (
    <Field label="Starts">
      {(ids) => (api.phone
        ? <PhonePicker label="Starts" field="time" value={String(start)} options={options} onChange={change} />
        : <KeySelect id={ids.id} label="Starts" value={String(start)} options={options} onChange={change} />)}
    </Field>
  )
}

// ---------------------------------------------------------------------------
// The confirmation (§12)
// ---------------------------------------------------------------------------

/** What the words around the switch need: Prefers, and consent already given for the mobile. */
interface ChannelFacts {
  prefers: Prefers | null
  mobile: string | null
  smsOk: { at: string; number: string } | null
}

function channelFacts(state: SheetState): ChannelFacts | null {
  const contact = state.contact
  if (contact.kind === 'chosen') {
    const known = contact.contact
    return { prefers: known.prefers, mobile: known.mobile, smsOk: known.smsOk }
  }
  if (contact.kind === 'new') {
    const typed = contact.fields
    return {
      prefers: typed.prefers,
      mobile: typed.phoneKind === 'mobile' && typed.phone.trim() ? typed.phone.trim() : null,
      smsOk: null,
    }
  }
  return null
}

const digitsOf = (text: string | null | undefined): string => (text ?? '').replace(/\D/g, '').slice(-10)

/** Bold the words that say when, inside the sentence the customer receives. */
function withBold(text: string, bold: string): { before: string; bold: string; after: string } {
  const at = text.indexOf(bold)
  if (at < 0) return { before: text, bold: '', after: '' }
  return { before: text.slice(0, at), bold, after: text.slice(at + bold.length) }
}

function Confirmation() {
  const api = useRequestSheet()
  const { setup, facts, phone } = api
  const appointment = api.state.appointment
  const contact = channelFacts(api.state)
  if (!contact || !appointment.date || appointment.start === null) return null

  /* The same reading the create call makes, so the switch never promises a channel it won't use. */
  const channel = channelFor(api.state, setup.smsReady)
  const noRecipient = channel.channel === null
  const on = appointment.confirm && !noRecipient
  const first = facts.contactFirstName ?? ''
  const prefers = contact.prefers ?? 'either'
  const viaEmail = channel.channel === 'email' || (noRecipient && prefers === 'email')
  const label = `${viaEmail ? 'Email' : 'Text'} ${first} a confirmation`
  const side = channel.channel === 'sms' ? `prefers ${PREFERS_LABELS[prefers]} · ${channel.to}`
    : channel.channel === 'email' ? `prefers ${PREFERS_LABELS[prefers]}` : null
  const window = setup.arrivalMinutes

  const words: ConfirmationFacts = {
    firstName: first,
    company: setup.company ?? 'us',
    salesperson: appointment.salespersonName ?? '',
    salespersonTitle: null,
    streetLine: facts.street ?? '',
    date: appointment.date,
    start: appointment.start,
    minutes: facts.minutes,
    jobTitle: facts.jobTitle ?? api.state.job.title,
    arrivalMinutes: window,
    phone: (facts.orgUnitId ? setup.phones.branches[facts.orgUnitId] : null) || setup.phones.company,
  }

  const agreed = contact.smsOk && digitsOf(contact.smsOk.number) === digitsOf(contact.mobile)
  const windowWords = ARRIVAL_WINDOWS.find((one) => one.minutes === window)?.label ?? `${window} min`
  const consent = agreed
    ? `${first} agreed to appointment texts on ${dayComma(contact.smsOk!.at.slice(0, 10))}.`
    : phone
      ? `Records that ${first} agreed to appointment texts, who recorded it and when.`
      : `Turning this on records that ${first} agreed to texts about appointments, with your name and the time.`
        + (window > 0 ? ` The ${windowWords} arrival window is the company’s (Settings → Sales coverage).` : '')

  return (
    <div>
      <div className="my-4 h-px bg-line-faint" />
      <div className="flex items-center gap-2.5">
        <Switch
          checked={on}
          onChange={(checked) => setAppointment(api.update, { confirm: checked })}
          label={label}
          hideLabel
          disabled={noRecipient}
        />
        <b className={cn('font-bold text-text', phone ? 'text-[14.5px]' : 'text-[13.5px]')}>{label}</b>
        {side && !phone && <span className="min-w-0 truncate text-[12px] text-text-muted">{side}</span>}
      </div>

      {on && channel.channel === 'sms' && (() => {
        const text = confirmationText(words)
        const parts = withBold(text, `${dayComma(appointment.date!)} ${arrivalWords(appointment.start!, window)}`)
        return (
          <>
            <div
              /* A message bubble: three round corners and the one it came from (Board 7). */
              style={{ borderRadius: '14px 14px 14px 4px' }}
              className={cn('mt-2.5 max-w-[544px] bg-hover px-3 py-2.5',
                'leading-[1.45] text-text', phone ? 'text-[13px]' : 'text-[12.5px]')}>
              {parts.before}<b>{parts.bold}</b>{phone ? '… Reply STOP to opt out.' : parts.after}
            </div>
            <div className="mt-2 text-[11.5px] leading-[1.45] text-text-muted">{consent}</div>
          </>
        )
      })()}

      {on && channel.channel === 'email' && (
        <EmailConfirmation words={words} to={channel.to!} window={window} />
      )}

      <ChannelBand band={channel.band} first={first} company={setup.company ?? 'your company'}
        byEmail={channel.channel === 'email'} />
    </div>
  )
}

/**
 * The email confirmation as the customer will read it — and editable (Aaron,
 * step 28): more addresses beside the contact's own, and its opening in other
 * words when the standard ones don't fit. The signature and the line about
 * changing the time stay as they are.
 */
function EmailConfirmation({ words, to, window }: {
  words: ConfirmationFacts; to: string; window: number
}) {
  const api = useRequestSheet()
  const appointment = api.state.appointment
  const email = confirmationEmail(words)
  const standard = email.lines[0]!
  const custom = appointment.confirmMessage
  const [adding, setAdding] = useState(false)
  const [typed, setTyped] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const arriving = window > 0
    ? `arriving ${betweenWords(appointment.start!, appointment.start! + window)}`
    : `arriving at ${clock(appointment.start!)}`
  const opening = withBold(standard, `${dayLong(appointment.date!)}, ${arriving}`)

  const add = () => {
    const address = typed.trim().toLowerCase()
    if (!address) { setAdding(false); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) { setProblem('That isn’t an email address.'); return }
    if (address === to.toLowerCase() || appointment.confirmAlsoTo.includes(address)) {
      setProblem('It already goes there.'); return
    }
    if (appointment.confirmAlsoTo.length >= 5) { setProblem('Five more addresses at most.'); return }
    setAppointment(api.update, { confirmAlsoTo: [...appointment.confirmAlsoTo, address] })
    setTyped('')
    setProblem(null)
  }
  const drop = (address: string) => setAppointment(api.update,
    { confirmAlsoTo: appointment.confirmAlsoTo.filter((one) => one !== address) })

  return (
    <div className="mt-2.5 rounded-[12px] border border-line bg-surface px-3.5 py-3 text-[12.5px] leading-[1.5] text-text">
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-text-muted">
        <span>To {to}</span>
        {appointment.confirmAlsoTo.map((address) => (
          <span key={address} className="inline-flex h-[22px] items-center gap-1 rounded-chip bg-surface-alt pl-2 pr-1 text-text">
            {address}
            <button type="button" aria-label={`Don’t send to ${address}`} onClick={() => drop(address)}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-text-muted hover:text-text">
              <IconClose size={11} />
            </button>
          </span>
        ))}
        {adding
          ? (
            <input
              autoFocus
              value={typed}
              type="email"
              aria-label="Another email address"
              placeholder="another address"
              onChange={(event) => { setTyped(event.target.value); setProblem(null) }}
              onBlur={add}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ',') { event.preventDefault(); add() }
                else if (event.key === 'Escape') { event.stopPropagation(); setAdding(false); setTyped(''); setProblem(null) }
              }}
              className="h-[22px] w-[200px] rounded-chip border border-accent-line bg-surface px-2 text-[11.5px] text-text outline-none focus-visible:shadow-none"
            />
          )
          : appointment.confirmAlsoTo.length < 5 && (
            <button type="button" onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 font-semibold text-accent hover:underline">
              <IconPlus size={12} />Add an address
            </button>
          )}
        <span className="basis-full" />
        <b className="text-text">{email.subject}</b>
      </div>
      {problem && <div className="mb-1.5 text-[11.5px] font-medium text-danger">{problem}</div>}

      {custom === null
        ? (
          <>
            {opening.before}<b>{opening.bold}</b>{opening.after}{' '}
            <button type="button"
              onClick={() => setAppointment(api.update, { confirmMessage: standard, confirmMessageFrom: standard })}
              className="text-[12px] font-semibold text-accent hover:underline">
              Edit the text
            </button>
          </>
        )
        : (
          <>
            <textarea
              value={custom}
              aria-label="The email’s message"
              onChange={(event) => setAppointment(api.update, { confirmMessage: event.target.value })}
              className={cn('h-[104px] w-full resize-none rounded-control border border-line bg-surface px-2.5 py-2',
                'text-[12.5px] leading-[1.5] text-text outline-none focus-visible:shadow-none',
                'focus:border-accent-line focus:ring-2 focus:ring-accent-ring')}
            />
            {appointment.confirmMessageFrom !== null && appointment.confirmMessageFrom !== standard && (
              <div className="mt-1 text-[11.5px] font-medium text-warn">
                The time or who goes changed after this was edited — check the words, or use the standard text.
              </div>
            )}
            <button type="button"
              onClick={() => setAppointment(api.update, { confirmMessage: null, confirmMessageFrom: null })}
              className="mt-1 text-[12px] font-semibold text-accent hover:underline">
              Use the standard text
            </button>
          </>
        )}

      <div className="mt-2.5 flex items-center gap-2.5">
        <Avatar name={words.salesperson} size={30} />
        <div className="flex flex-col">
          <b className="text-[12.5px]">{words.salesperson}</b>
          {email.lines[2] && <span className="text-[11.5px] text-text-muted">{email.lines[2]}</span>}
        </div>
      </div>
      <div className="mt-2.5 text-[12px] text-text-muted">{email.lines[3]}</div>
    </div>
  )
}

/** Board 9 B's two bands, and the two §12.1 adds. */
function ChannelBand({ band, first, company, byEmail }: {
  band: ConfirmChannel['band']
  first: string
  company: string
  byEmail: boolean
}) {
  if (!band) return null
  const tail = byEmail ? '— the confirmation goes by email.' : '— a confirmation can’t be sent.'
  if (band === 'no_recipient') {
    return <Band tone="grey" title="No mobile number and no email" body="— a confirmation can’t be sent." />
  }
  if (band === 'no_mobile') {
    return <Band tone="slate" title={`${first} prefers Text but has no mobile number`} body={tail} />
  }
  if (band === 'opted_out') {
    return <Band tone="slate" title={`${first} has stopped texts from you`} body={tail} />
  }
  return <Band tone="slate" title={`Texts aren’t set up for ${company} yet`} body={tail} />
}
